import {Col, Divider, Row} from "antd";
import {useTranslation} from "react-i18next";
import FieldBuilder from "../../form-components/field-builder";
import {languages} from "../../../utils/helpers/constants";
import {MediumFor} from "../../../models/medium/enum";
import DiscountCodeContext from "../../../context/discount-code/context";
import {useContext, useState} from "react";
import EndPoints from "../../../services/end-points";
import {IMedium} from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import {errorNotification, successNotification} from "../../../utils/helpers/notification";

export const AdditionalSettingsSection = ({setValue}) => {
    const {t} = useTranslation();
    const {details} = useContext(DiscountCodeContext);
    const [newImages, setNewImages] = useState<Record<string, string | null>>({});
    const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
    const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
    const [withoutBackgrounds, setWithoutBackgrounds] = useState<Record<string, boolean>>({});
    const handleImageChange = (fieldName: string) => (response: IMedium | null) => {
        setNewImages((prev) => ({...prev, [fieldName]: response?.url ?? null}));
        setImageChanged((prev) => ({...prev, [fieldName]: true}));
    };

    const handleUploadNewImage = async (fieldName: string) => {
        setImageLoading(prev => ({...prev, [fieldName]: true}));
        const newImageUrl = newImages[fieldName];
        if (!newImageUrl) return;

        try {
            const res = await fetch(newImageUrl);
            const blob = await res.blob();
            const file = new File([blob], `${fieldName}.png`, {type: "image/png"});

            const formData = new FormData();
            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            formData.append("without_background", String(withoutBackgrounds[fieldName] ?? false));

            // Add is_notification parameter for notify_image field
            if (fieldName === "notifyImageId") {
                formData.append("is_notification", "1");
            }

            const uploadRes = await EndPoints.media.uploadMedia(formData);
            setValue(fieldName, uploadRes.id, {shouldDirty: true});
            setImageChanged((prev) => ({...prev, [fieldName]: false}));
            successNotification(t(`image_uploaded_successfully`))
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setImageLoading(prev => ({...prev, [fieldName]: false}));

        }
    };

    const handleUpdateBackground = async (fieldName: string, imageId: number) => {
        try {
            await EndPoints.media.updateMedia(imageId, Boolean(withoutBackgrounds[fieldName]), true);
            successNotification("تم تحديث نوع الخلفية بنجاح");
        } catch (err) {
            errorNotification("فشل التحديث");
        }
    };

    const handleDeleteImage = async (fieldName: string) => {
        try {
            setImageLoading(prev => ({...prev, [fieldName]: true}));
            setValue(fieldName, null);
        } finally {
            setImageLoading(prev => ({...prev, [fieldName]: false}));
        }
    };


    return (
        <>
            <Divider orientation="center">{t("additional_settings")}</Divider>
            <Row gutter={[16, 8]}>
                <Col span={24}>
                    <FieldBuilder name="message" label={t("message")} input={{type: "text-area"}}/>
                </Col>
                {languages?.map((lang, index) => (
                    <Col span={12} key={index}>
                        <FieldBuilder
                            name={`note[${lang?.code}]`}
                            label={`${t("note")} (${lang?.name})`}
                            input={{type: "text-area"}}
                            rules={{required: false}}
                        />
                    </Col>
                ))}
                <Col span={24}>
                    <ImageFieldBlock
                        fieldName="imageId"
                        currentId={details?.image?.id}
                        fieldValue={withoutBackgrounds["imageId"]}
                        currentImage={details?.image}
                        newImageUrl={newImages["imageId"] ?? null}
                        isChanged={imageChanged["imageId"]}
                        onUpload={() => handleUploadNewImage("imageId")}
                        onUpdateBackground={() => handleUpdateBackground("imageId", details?.image?.id)}
                        onImageChange={handleImageChange("imageId")}
                        setWithoutBackgrounds={(val) => setWithoutBackgrounds(prev => ({...prev, imageId: val}))}
                        onDeleteImage={() => handleDeleteImage("imageId")}
                        isImageLoading={imageLoading["imageId"]}
                        value={withoutBackgrounds["imageId"]}
                        force={true}
                        notRequired={true}
                    />
                </Col>
                <Col span={24}>
                    <ImageFieldBlock
                        title="notify_image"
                        fieldName="notifyImageId"
                        currentId={details?.notifyImage?.id}
                        fieldValue={withoutBackgrounds["notifyImageId"]}
                        currentImage={details?.notifyImage}
                        newImageUrl={newImages["notifyImageId"] ?? null}
                        isChanged={imageChanged["notifyImageId"]}
                        onUpload={() => handleUploadNewImage("notifyImageId")}
                        onUpdateBackground={() => handleUpdateBackground("notifyImageId", details?.notifyImage?.id)}
                        onImageChange={handleImageChange("notifyImageId")}
                        setWithoutBackgrounds={(val) => setWithoutBackgrounds(prev => ({...prev, notifyImageId: val}))}
                        onDeleteImage={() => handleDeleteImage("notifyImageId")}
                        isImageLoading={imageLoading["notifyImageId"]}
                        value={withoutBackgrounds["notifyImageId"]}
                        force={true}
                        notRequired={true}
                    />
                </Col>
            </Row>
        </>
    );
};